import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AwaitingSamplesProps } from './AwaitingSamples.types';
import { useModal } from '@components/hooks';

import { Box } from '@base-app';
import { CardGrid, InfoCard } from '@builder';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import {
  StyledAtHomeIcon,
  StyledAwaitingSamplesContainer,
  StyledInPersonIcon,
  StyledTitle,
} from './AwaitingSamples.styles';

import SampleComposition from '../../shared/SampleComposition/SampleComposition';
import SampleTitle from '../../shared/SampleTitle/SampleTitle';
import SampleTotal from '../../shared/SampleTotal/SampleTotal';
import ViewOrderIdsModal from '../../shared/ViewOrderIds/ViewOrderIdsModal/ViewOrderIdsModal';

const AwaitingSamples: FC<AwaitingSamplesProps> = ({ data }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { isShowing, toggle } = useModal();
  const isSeriesEmptyInPerson = data.IN_PERSON === undefined;
  const isSeriesEmptyAtHome = data.AT_HOME === undefined;

  return (
    <Box>
      <Box>
        <CardGrid
          title={<SampleTitle id="awaiting-samples" label={t('awaiting_samples_data')} onClickViewOrderIds={toggle} />}
        >
          <StyledAwaitingSamplesContainer css={{ maxHeight: '192px' }}>
            <InfoCard css={{ minWidth: '330px' }} separator="right">
              <SampleTotal
                id="awaiting-samples"
                description={t('awaiting_received')}
                title={t('total_awaiting_samples')}
                totalSamples={data?.total ?? t('data_unavailable')}
              />
            </InfoCard>
            <InfoCard
              css={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: '330px',
              }}
              separator="right"
            >
              <StyledTitle data-testid="awaiting-sample-title"> {t('in_person').toLocaleUpperCase()} </StyledTitle>
              <SampleComposition
                details={t('of_awaiting_samples')}
                icon={<StyledInPersonIcon />}
                id="in-person"
                isSeriesEmpty={isSeriesEmptyInPerson}
                percentage={data?.IN_PERSON?.percentage?.toFixed(0)}
                total={data?.IN_PERSON?.total}
              />
            </InfoCard>
            <InfoCard
              css={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: '330px',
              }}
            >
              <StyledTitle data-testid="awaiting-sample-title"> {t('at_home').toLocaleUpperCase()} </StyledTitle>
              <SampleComposition
                details={t('of_awaiting_samples')}
                icon={<StyledAtHomeIcon />}
                id="at-home"
                isSeriesEmpty={isSeriesEmptyAtHome}
                percentage={data?.AT_HOME?.percentage?.toFixed(0)}
                total={data?.AT_HOME?.total}
              />
            </InfoCard>
          </StyledAwaitingSamplesContainer>
        </CardGrid>
      </Box>
      {isShowing && (
        <ViewOrderIdsModal
          isOpen={isShowing}
          onClose={() => toggle()}
          title={t('view_order_id.title.awaiting_samples')}
        />
      )}
    </Box>
  );
};

export default AwaitingSamples;
